import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, Alert, Button, Divider,
  Accordion, AccordionSummary, AccordionDetails, Chip
} from '@mui/material';
import { Dashboard, ArrowBack, ExpandMore } from '@mui/icons-material';
import api from '../../services/api';

const karatMap = { '22K': '22K', '22/22K': '22K', '24K': '24K', '24/24K': '24K', '18K': '18K', 'PT95': 'PT95', 'WG': 'WG' };
const karatOrder = ['22K', '24K', '18K', 'PT95', 'WG'];

const n = (v) => parseFloat(v) || 0;
const fmt = (v) => n(v).toFixed(3);

// Compact table header cell
const TH = ({ children, align = 'right' }) => (
  <TableCell align={align} sx={{ fontWeight: 700, fontSize: '0.7rem', p: '4px 6px', bgcolor: '#1976d2', color: '#fff', whiteSpace: 'nowrap' }}>
    {children}
  </TableCell>
);
// Compact table data cell
const TD = ({ children, align = 'right', color }) => (
  <TableCell align={align} sx={{ fontSize: '0.72rem', p: '3px 6px', color: color || 'inherit', fontWeight: color ? 700 : 400 }}>
    {children}
  </TableCell>
);

const SectionTitle = ({ num, title, color = '#1976d2' }) => (
  <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 36, bgcolor: color + '18', '& .MuiAccordionSummary-content': { my: 0.5 } }}>
    <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color }}>
      {num}. {title}
    </Typography>
  </AccordionSummary>
);

const KarigarOverview = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const karigarParam = params.get('karigar') || '';
  const karatParam = params.get('karat') || '';

  const [karigars, setKarigars] = useState([]);
  const [metalIssues, setMetalIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [overviewRes, metalRes] = await Promise.all([
          api.get(`/api/karigar/overview-summary${karigarParam ? `?karigar=${karigarParam}` : ''}`),
          api.get(`/api/karigar/metal-issue-list${karigarParam ? `?karigar=${karigarParam}` : ''}`)
        ]);
        setKarigars(overviewRes.data || []);
        setMetalIssues(Array.isArray(metalRes.data) ? metalRes.data : []);
      } catch (err) {
        setError('Failed to load data: ' + (err.response?.data?.message || err.message));
      }
      setLoading(false);
    };
    fetchAll();
  }, [karigarParam]);

  // Build per-karigar metal issue totals by karat
  const getMetalIssueTotals = (abbreviation) => {
    const totals = {};
    metalIssues.filter(m => m.karigarAbbreviation === abbreviation).forEach(m => {
      const k = karatMap[m.karat] || m.karat;
      totals[k] = (totals[k] || 0) + n(m.amount);
    });
    return totals;
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <CircularProgress />
    </Box>
  );

  if (error) return <Box sx={{ p: 4 }}><Alert severity="error">{error}</Alert></Box>;

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', background: '#f4f6fa', pb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: '#1976d2', px: 3, py: 1.5, position: 'sticky', top: 0, zIndex: 10 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Dashboard sx={{ fontSize: 32, color: '#fff' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>
            Karigar Metal & Work Tracking Dashboard
            {karigarParam && <Chip label={karigarParam} size="small" sx={{ ml: 1, bgcolor: '#fff', color: '#1976d2', fontWeight: 700 }} />}
            {karatParam && <Chip label={karatParam} size="small" sx={{ ml: 1, bgcolor: '#fff3e0', color: '#e65100', fontWeight: 700 }} />}
          </Typography>
        </Box>
        <Button startIcon={<ArrowBack />} onClick={() => window.history.back()} variant="outlined" sx={{ color: '#fff', borderColor: '#fff' }}>
          Back
        </Button>
      </Box>

      <Box sx={{ px: 2, pt: 2 }}>
        {karigars.length === 0 && (
          <Alert severity="info">No karigar data found.</Alert>
        )}
        {karigars.map((karigar) => (
          <KarigarCard key={karigar.abbreviation} karigar={karigar} metalIssueTotals={getMetalIssueTotals(karigar.abbreviation)} karatFilter={karatParam} />
        ))}
      </Box>
    </Box>
  );
};

const KarigarCard = ({ karigar, metalIssueTotals, karatFilter }) => {
  // Filter karats to show
  const activeKarats = karatFilter
    ? karatOrder.filter(k => k === (karatMap[karatFilter] || karatFilter))
    : karatOrder;

  // Raw data from API
  const given = karigar.totalGiven || {};       // { '22K': 100, ... }
  const received = karigar.totalReceived || {}; // { '22K': 80, ... }
  const workReceiptDetails = karigar.workReceiptDetails || {};
  const wastageSummary = karigar.wastageSummary || {};
  const workOutstanding = karigar.workOutstanding || {};
  const itemOutstanding = karigar.itemOutstanding || {};

  // Per-karat calculations
  const rows = activeKarats.map(karat => {
    const metalIssueWt = n(metalIssueTotals[karat]);       // from MetalIssue records
    const workIssueWt = n(workOutstanding[karat]?.totalWeight) + n(workReceiptDetails[karat]?.totalWeight); // total ever issued for work
    const wrDetails = workReceiptDetails[karat] || {};
    const workReceipt = n(wrDetails.totalWeight);           // gross receipt weight
    const gsbWeight = 0;                                    // not tracked separately yet
    const netRcvdWt = workReceipt - gsbWeight;
    const wrWastage = n(wastageSummary[karat]);
    const wastagePercent = workReceipt > 0 ? (wrWastage / workReceipt * 100) : 0;
    const grossReceiptWt = workReceipt + wrWastage;
    const netReceiptBal = n(given[karat]) - netRcvdWt;
    const grossReceiptBal = n(given[karat]) - grossReceiptWt;
    const netMetalIssue = metalIssueWt - workReceipt;
    const lossIssueWt = 0;                                  // placeholder
    const balLoss = wrWastage;
    const netBalLoss = netReceiptBal - balLoss;
    const actNetMetalIssue = metalIssueWt - grossReceiptWt;
    const metalIssueDue = metalIssueWt - grossReceiptWt;
    const metalOutstanding = n(given[karat]) - n(received[karat]);
    const workOutstandingWt = n(workOutstanding[karat]?.totalWeight);
    const workOutstandingCount = n(workOutstanding[karat]?.count);
    const itemOutstandingWt = n(itemOutstanding[karat]?.totalWeight);
    const itemOutstandingCount = n(itemOutstanding[karat]?.count);

    return {
      karat,
      // Section 1 - Metal Issue Due
      metalIssueWt, workIssueWt, metalIssueDue, netReceiptBal,
      netMetalIssue, netBalLoss, actNetMetalIssue,
      // Section 2 - Work Receipt
      workReceipt, netRcvdWt, wrWastage, wastagePercent,
      grossReceiptWt, grossReceiptBal,
      // Section 3 & 4 - Wastage
      lossIssueWt, balLoss,
      // Section 5 - Metal Outstanding
      metalOutstanding,
      // Section 6 - Work Outstanding
      workOutstandingWt, workOutstandingCount,
      // Section 7 - Item Outstanding
      itemOutstandingWt, itemOutstandingCount,
    };
  }).filter(r => r.metalIssueWt > 0 || r.workReceipt > 0 || r.workOutstandingCount > 0 || r.itemOutstandingCount > 0);

  return (
    <Paper elevation={2} sx={{ mb: 3, overflow: 'hidden', borderRadius: 2 }}>
      {/* Karigar Header */}
      <Box sx={{ px: 2, py: 1.2, background: 'linear-gradient(90deg, #1565c0 0%, #1976d2 100%)', color: '#fff', display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {karigar.abbreviation} — {karigar.name}
        </Typography>
      </Box>

      {rows.length === 0 ? (
        <Box sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">No transaction data available.</Typography></Box>
      ) : (
        <Box sx={{ p: 1 }}>

          {/* SECTION 1: Metal Issue Due Details */}
          <Accordion defaultExpanded>
            <SectionTitle num={1} title="Metal Issue Due Details" color="#1565c0" />
            <AccordionDetails sx={{ p: 1 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TH align="left">Base Karat</TH>
                      <TH>WorkIssueWt</TH>
                      <TH>MetalIssueWt</TH>
                      <TH>MetalIssueDue</TH>
                      <TH>NetReceiptBal</TH>
                      <TH>NetMetalIssue</TH>
                      <TH>NetBalLoss</TH>
                      <TH>ActNetMetalIssue</TH>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map(r => (
                      <TableRow key={r.karat} hover>
                        <TD align="left">{r.karat}</TD>
                        <TD>{fmt(r.workIssueWt)}</TD>
                        <TD>{fmt(r.metalIssueWt)}</TD>
                        <TD color={r.metalIssueDue > 0 ? '#c62828' : undefined}>{fmt(r.metalIssueDue)}</TD>
                        <TD>{fmt(r.netReceiptBal)}</TD>
                        <TD>{fmt(r.netMetalIssue)}</TD>
                        <TD color={r.netBalLoss < 0 ? '#c62828' : undefined}>{fmt(r.netBalLoss)}</TD>
                        <TD>{fmt(r.actNetMetalIssue)}</TD>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>

          {/* SECTION 2: Work Receipt Details */}
          <Accordion defaultExpanded>
            <SectionTitle num={2} title="Work Receipt Details" color="#2e7d32" />
            <AccordionDetails sx={{ p: 1 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TH align="left">Base Karat</TH>
                      <TH>WorkReceipt</TH>
                      <TH>NetRcvdWt</TH>
                      <TH>NetReceiptBal</TH>
                      <TH>WRWastage</TH>
                      <TH>Wastage%</TH>
                      <TH>GrossReceiptWt</TH>
                      <TH>GrossReceiptBal</TH>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map(r => (
                      <TableRow key={r.karat} hover>
                        <TD align="left">{r.karat}</TD>
                        <TD>{fmt(r.workReceipt)}</TD>
                        <TD>{fmt(r.netRcvdWt)}</TD>
                        <TD>{fmt(r.netReceiptBal)}</TD>
                        <TD>{fmt(r.wrWastage)}</TD>
                        <TD>{r.wastagePercent.toFixed(2)}%</TD>
                        <TD>{fmt(r.grossReceiptWt)}</TD>
                        <TD color={r.grossReceiptBal < 0 ? '#c62828' : '#2e7d32'}>{fmt(r.grossReceiptBal)}</TD>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>

          {/* SECTION 3: Wastage Details */}
          <Accordion>
            <SectionTitle num={3} title="Wastage Details" color="#e65100" />
            <AccordionDetails sx={{ p: 1 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TH align="left">Base Karat</TH>
                      <TH>NetRcvdWt</TH>
                      <TH>Wastage%</TH>
                      <TH>WRWastage</TH>
                      <TH>LossIssueWt</TH>
                      <TH>BalLoss</TH>
                      <TH>NetReceiptBal</TH>
                      <TH>NetBalLoss</TH>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map(r => (
                      <TableRow key={r.karat} hover>
                        <TD align="left">{r.karat}</TD>
                        <TD>{fmt(r.netRcvdWt)}</TD>
                        <TD>{r.wastagePercent.toFixed(2)}%</TD>
                        <TD>{fmt(r.wrWastage)}</TD>
                        <TD>{fmt(r.lossIssueWt)}</TD>
                        <TD>{fmt(r.balLoss)}</TD>
                        <TD>{fmt(r.netReceiptBal)}</TD>
                        <TD color={r.netBalLoss < 0 ? '#c62828' : undefined}>{fmt(r.netBalLoss)}</TD>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>

          {/* SECTION 4: Wastage Summary */}
          <Accordion>
            <SectionTitle num={4} title="Wastage Summary" color="#6a1b9a" />
            <AccordionDetails sx={{ p: 1 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TH align="left">Base Karat</TH>
                      <TH>NetRcvdWt</TH>
                      <TH>Wastage%</TH>
                      <TH>WRWastage (Total)</TH>
                      <TH>LossIssueWt</TH>
                      <TH>BalLoss</TH>
                      <TH>NetReceiptBal</TH>
                      <TH>NetBalLoss</TH>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map(r => (
                      <TableRow key={r.karat} hover>
                        <TD align="left">{r.karat}</TD>
                        <TD>{fmt(r.netRcvdWt)}</TD>
                        <TD>{r.wastagePercent.toFixed(2)}%</TD>
                        <TD>{fmt(r.wrWastage)}</TD>
                        <TD>{fmt(r.lossIssueWt)}</TD>
                        <TD>{fmt(r.balLoss)}</TD>
                        <TD>{fmt(r.netReceiptBal)}</TD>
                        <TD color={r.netBalLoss < 0 ? '#c62828' : undefined}>{fmt(r.netBalLoss)}</TD>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>

          {/* SECTION 5: Metal Outstanding */}
          <Accordion defaultExpanded>
            <SectionTitle num={5} title="Metal Outstanding" color="#00695c" />
            <AccordionDetails sx={{ p: 1 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TH align="left">Base Karat</TH>
                      <TH>Metal Issued</TH>
                      <TH>Work Received</TH>
                      <TH>Metal Outstanding</TH>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map(r => (
                      <TableRow key={r.karat} hover>
                        <TD align="left">{r.karat}</TD>
                        <TD>{fmt(r.metalIssueWt)}</TD>
                        <TD>{fmt(r.grossReceiptWt)}</TD>
                        <TD color={r.metalOutstanding > 0 ? '#c62828' : '#2e7d32'}>{fmt(r.metalOutstanding)}</TD>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>

          {/* SECTION 6: Work Outstanding */}
          <Accordion defaultExpanded>
            <SectionTitle num={6} title="Work Outstanding" color="#1565c0" />
            <AccordionDetails sx={{ p: 1 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TH align="left">Base Karat</TH>
                      <TH>Pending Items</TH>
                      <TH>Total Weight (g)</TH>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map(r => (
                      <TableRow key={r.karat} hover>
                        <TD align="left">{r.karat}</TD>
                        <TD color={r.workOutstandingCount > 0 ? '#c62828' : undefined}>{r.workOutstandingCount}</TD>
                        <TD>{fmt(r.workOutstandingWt)}</TD>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>

          {/* SECTION 7: Item Outstanding Details */}
          <Accordion>
            <SectionTitle num={7} title="Item Outstanding Details" color="#4527a0" />
            <AccordionDetails sx={{ p: 1 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TH align="left">Base Karat</TH>
                      <TH>Pending Items</TH>
                      <TH>Total Weight (g)</TH>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map(r => (
                      <TableRow key={r.karat} hover>
                        <TD align="left">{r.karat}</TD>
                        <TD color={r.itemOutstandingCount > 0 ? '#e65100' : undefined}>{r.itemOutstandingCount}</TD>
                        <TD>{fmt(r.itemOutstandingWt)}</TD>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>

        </Box>
      )}
    </Paper>
  );
};

export default KarigarOverview;
